import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, first, Observable, of } from 'rxjs';
import { generateIndexDBKey } from 'src/app/shared/utils/indexdb.utils';
import { environment } from 'src/environments/environment';
import { HTTP_TICKET_EVENTS_EVENT } from '../../enums/events-buffer/endpoints/http-ticket-event-event.enum';
import { HTTP_METHOD } from '../../enums/http-methods.enum';
import { Logger } from '../../interfaces/Logger';
import { SocketService } from '../../services/socket/socket.service';
import { EVENT_BUFFER_PROTOCOL } from '../event-buffer/enums/event-protocol.enum';
import { TicketEvent } from './interface/ticket-event.model';

/**
 * Service responsible for handling socket communications for ticket events
 * Provides methods to send and update ticket events through WebSockets
 */
@Injectable({
  providedIn: 'root',
})
export class TicketEventSocketHandlerService {
  private readonly socketService = inject(SocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);

  /**
   * Sends a request to update a ticket event through WebSockets
   * 
   * @param ticketEvent - The ticket event to be updated
   * @returns An Observable that emits true if the update was successful, false otherwise
   */
  sendUpdateTicketEventEvent(ticketEvent: TicketEvent): Observable<boolean> {
    return this.socketService
      .prepareToEmitEvent<boolean>({
        _id: generateIndexDBKey(),
        name: HTTP_TICKET_EVENTS_EVENT.updateTicketEvent,
        payload: {
          ticket_event: ticketEvent.serialize(),
        },
        protocol: EVENT_BUFFER_PROTOCOL.http,
        http: {
          method: HTTP_METHOD.POST,
          url: `${environment.apiURL}${HTTP_TICKET_EVENTS_EVENT.updateTicketEvent}`,
        },
      })
      .pipe(
        first(),
        catchError((error) => {
          this.logger.error(
            new Error(
              '[TicketEventSocketHandler][sendUpdateTicketEventEvent] Error sending update ticket event'
            ),
            error
          );

          return of(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      );
  }
}
